/**
 * CompleteReviewDialog — explicit Mark Reviewed step (Feb 2026 · sprint 374).
 *
 * Fetches /reviews/{id}/summary for counts, lets the user add optional
 * participant names, then calls /reviews/{id}/complete which locks
 * decisions, ends the meeting session and flips status → reviewed.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { CheckCircle, X, AlertTriangle } from 'lucide-react';

export function CompleteReviewDialog({ reviewId, onCancel, onCompleted }) {
  const { API, getHeaders } = useAuth();
  const [summary, setSummary] = useState(null);
  const [participants, setParticipants] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/reviews/${reviewId}/summary`, { headers: getHeaders() });
        if (r.ok) setSummary(await r.json());
      } catch { /* silent */ }
    })();
  }, [API, getHeaders, reviewId]);

  const submit = async () => {
    setSaving(true);
    try {
      const list = participants.split(',').map(s => s.trim()).filter(Boolean);
      const r = await fetch(`${API}/reviews/${reviewId}/complete`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ participants: list }),
      });
      if (!r.ok) throw new Error();
      const body = await r.json();
      toast.success('Review marked reviewed');
      onCompleted?.(body);
    } catch { toast.error('Failed to complete review'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="complete-review-dialog">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E7EBE6]">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-[color:var(--color-primary)]"/>
            <h2 className="text-base font-semibold text-[color:var(--color-text-primary)]">Complete Review</h2>
          </div>
          <button onClick={onCancel} data-testid="cr-cancel-x" className="text-[#7A8B82] hover:text-red-600">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          {summary && (
            <div className="grid grid-cols-2 gap-2 text-xs" data-testid="cr-summary">
              <SummaryRow label="Notes"                    value={summary.notes}                     testid="cr-notes-count"/>
              <SummaryRow label="Decisions"                value={summary.decisions}                 testid="cr-decisions-count"/>
              <SummaryRow label="New Actions"              value={summary.new_actions}               testid="cr-new-actions-count"/>
              <SummaryRow label="Previous Actions Closed"  value={summary.previous_actions_closed}   testid="cr-closed-count"/>
              <SummaryRow label="Remaining Open"           value={summary.remaining_open_actions}    testid="cr-remaining-count"/>
              <SummaryRow label="Overdue" value={summary.overdue_actions} testid="cr-overdue-count"
                          highlight={summary.overdue_actions > 0}/>
            </div>
          )}
          {summary?.overdue_actions > 0 && (
            <div className="flex items-start gap-1.5 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-800">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
              <span>You have overdue actions. They will remain live after completion — please review before locking.</span>
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#7A8B82] block mb-1">Participants (optional, comma-separated)</label>
            <Input value={participants} onChange={(e) => setParticipants(e.target.value)}
                   placeholder="Rohan (Ops), Priya (Purchase)" data-testid="cr-participants" className="h-8 text-xs"/>
          </div>
          <div className="text-[11px] text-[#7A8B82] bg-[#F9FBF7] border border-[#EEF1EC] rounded p-2">
            On Mark Reviewed: decisions become locked (clarifications still possible),
            the active meeting session ends, and status becomes <b>Reviewed</b>. Actions remain live.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#E7EBE6] bg-[#FDFDFC]">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="cr-cancel"
                  className="h-8 text-xs">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving} data-testid="cr-confirm"
                  className="h-8 text-xs bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1">
            <CheckCircle className="w-3.5 h-3.5"/> {saving ? 'Saving…' : 'Mark Reviewed'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, highlight, testid }) {
  return (
    <div className={`flex items-center justify-between px-2 py-1.5 rounded border ${
      highlight ? 'bg-amber-50 border-amber-200' : 'bg-[#F9FBF7] border-[#EEF1EC]'}`} data-testid={testid}>
      <span className="text-[#5C6B64]">{label}</span>
      <span className={`font-semibold ${highlight ? 'text-amber-700' : 'text-[#1A2421]'}`}>{value ?? 0}</span>
    </div>
  );
}
